import { Box, Chip, Typography, useTheme } from "@mui/material";
import { BasicPieChart } from "@aclymatepackages/modules";
import { AclymateFooter } from "@aclymatepackages/net-zero";
import { formatDecimal } from "@aclymatepackages/formatters";
import { subcategoriesWithColors } from "@aclymatepackages/subcategories";
import { hexToRgba } from "@aclymatepackages/converters";

import Link from "../../atoms/mui/Link";
import { useDynamicWrappingPages } from "../../../helpers/components/report";

const PAGE_WIDTH = "8.5in";
const PAGE_HEIGHT = "11in";
const MAX_CATEGORIES = 10;
const CONFIDENCE_HELPER_TEXT =
  "this is a directional estimate based on survey responses. Connect real business data in Navigator to improve confidence.";
const TALK_TO_EXPERT_URL =
  "https://meetings.hubspot.com/aclymate/meeting-with-aclymate";
const UPGRADE_URL = "https://app.aclymate.com/explorer/upgrade";

const normalizeKey = (value) =>
  (value || "").toString().trim().toLowerCase().replace(/\s+/g, "-");

const subcategoryColorByName = subcategoriesWithColors.reduce(
  (acc, { name, subcategory, color }) => ({
    ...acc,
    [normalizeKey(name)]: color,
    [normalizeKey(subcategory)]: color
  }),
  {}
);

const buildFallbackPalette = (palette) => [
  palette.primary.main,
  palette.secondary.main,
  palette.primary.dark,
  palette.secondary.dark,
  palette.flights?.main || palette.primary.light,
  palette.employees?.main || palette.secondary.light,
  palette.offices?.main || palette.backgroundGray.dark,
  palette.vendors?.main || palette.text.secondary
];

const colorForSlice = ({ label, categoryKey }, idx, fallback) => {
  const fromKey = subcategoryColorByName[normalizeKey(categoryKey)];
  const fromLabel = subcategoryColorByName[normalizeKey(label)];
  return fromKey || fromLabel || fallback[idx % fallback.length];
};

const muteSlices = (data) =>
  data.map((slice) => ({
    ...slice,
    color: hexToRgba(slice.color, 0.7) || slice.color
  }));

const collapseExtras = (series) => {
  if (series.length <= MAX_CATEGORIES) {
    return series;
  }
  const head = series.slice(0, MAX_CATEGORIES - 1);
  const tail = series.slice(MAX_CATEGORIES - 1);
  const otherValue = tail.reduce(
    (sum, { value }) => sum + (Number(value) || 0),
    0
  );
  return [...head, { label: "Other", value: otherValue, categoryKey: "other" }];
};

const buildSliceData = (series, palette) => {
  const fallback = buildFallbackPalette(palette);
  const total = series.reduce(
    (sum, { value }) => sum + (Number(value) || 0),
    0
  );
  return series.map((slice, idx) => ({
    ...slice,
    color: colorForSlice(slice, idx, fallback),
    percent: total > 0 ? ((Number(slice.value) || 0) / total) * 100 : 0
  }));
};

const PdfPage = ({ palette, children }) => (
  <Box
    style={{
      width: PAGE_WIDTH,
      height: PAGE_HEIGHT,
      overflow: "hidden",
      backgroundColor: palette.common.white,
      pageBreakAfter: "always",
      breakAfter: "page"
    }}
    display="flex"
    flexDirection="column"
  >
    {children}
  </Box>
);

const PageHeader = ({ companyName, year, palette }) => (
  <Box
    px={5}
    pt={3}
    pb={2}
    display="flex"
    justifyContent="space-between"
    alignItems="baseline"
    style={{
      borderBottom: `1px solid ${palette.backgroundGray.main}`
    }}
  >
    <Typography variant="overline" color="primary">
      Aclymate Explorer
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {`${companyName} — Footprint Report ${year}`}
    </Typography>
  </Box>
);

const CoverPage = ({
  companyName,
  generatedDate,
  year,
  totalTons,
  palette
}) => (
  <PdfPage palette={palette}>
    <Box
      px={5}
      pt={6}
      pb={3}
      style={{ borderBottom: `2px solid ${palette.primary.main}` }}
    >
      <Typography variant="overline" color="primary">
        Aclymate Explorer
      </Typography>
      <Typography variant="h3" color="textPrimary">
        {companyName}
      </Typography>
      <Typography variant="h6" color="textSecondary">
        {`Footprint Report — ${year}`}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {`Generated ${generatedDate}`}
      </Typography>
    </Box>
    <Box
      mx={5}
      mt={4}
      p={3}
      style={{
        backgroundColor: palette.backgroundGray.light,
        borderLeft: `4px solid ${palette.primary.main}`,
        borderRadius: "4px"
      }}
    >
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Chip
          label="Confidence: Low"
          color="primary"
          size="small"
          style={{ backgroundColor: palette.primary.main }}
        />
        <Typography variant="subtitle1" color="textPrimary">
          Directional Estimate
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary">
        {CONFIDENCE_HELPER_TEXT}
      </Typography>
    </Box>
    <Box mx={5} mt={5} display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="overline" color="textSecondary">
        {`Estimated annual footprint — ${year}`}
      </Typography>
      <Box display="flex" alignItems="baseline" gap={1}>
        <Typography variant="h1" color="primary">
          {formatDecimal(Number(totalTons) || 0)}
        </Typography>
        <Typography variant="h4" color="textSecondary">
          tCO2e
        </Typography>
      </Box>
    </Box>
    <Box
      mx={5}
      mt={5}
      p={3}
      style={{
        backgroundColor: palette.backgroundGray.light,
        borderRadius: "4px"
      }}
    >
      <Typography variant="subtitle2" color="textPrimary" gutterBottom>
        About this estimate
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Aclymate Explorer estimates your footprint from the responses you gave
        in the emissions survey, applying industry-average factors for any spend
        categories you did not confirm individually. This is a directional
        estimate — useful for understanding scale and where to focus — not a
        measurement-grade inventory. Connect bills, transactions, and
        integrations in Aclymate Navigator to move from estimate to measurement.
      </Typography>
    </Box>
  </PdfPage>
);

const SliceRow = ({ slice, palette }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={1.5}
    py={0.75}
    style={{
      borderBottom: `1px solid ${palette.backgroundGray.main}`
    }}
  >
    <Box
      style={{
        width: 12,
        height: 12,
        borderRadius: 2,
        backgroundColor: slice.color,
        flexShrink: 0
      }}
    />
    <Box flexGrow={1}>
      <Typography variant="body2" color="textPrimary">
        {slice.label}
      </Typography>
    </Box>
    <Typography variant="body2" color="textPrimary">
      {`${formatDecimal(Number(slice.value) || 0)} tCO2e`}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {`${formatDecimal(Number(slice.percent) || 0)}%`}
    </Typography>
  </Box>
);

const BreakdownBlock = ({ title, data, palette }) => {
  const muted = muteSlices(data);
  return (
    <Box mx={5} mt={3}>
      <Typography variant="h5" color="textPrimary" gutterBottom>
        {title}
      </Typography>
      <Box
        display="flex"
        gap={4}
        alignItems="flex-start"
        style={{ width: "100%" }}
        mt={1}
      >
        <Box style={{ width: "45%" }}>
          <BasicPieChart data={muted} aspect={1.2} />
        </Box>
        <Box style={{ width: "55%" }}>
          {muted.map((slice, idx) => (
            <SliceRow key={`pie-row-${idx}`} slice={slice} palette={palette} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const CompletenessBlock = ({ completenessSummary, palette }) => {
  const items = completenessSummary?.items || [];
  return (
    <Box mx={5} mt={3}>
      <Typography variant="h5" color="textPrimary" gutterBottom>
        {`Data completeness — ${completenessSummary?.percent || 0}% complete`}
      </Typography>
      <Box mt={1} mb={2} display="flex" gap={3}>
        <Typography variant="body2" color="textSecondary">
          {`${completenessSummary?.skippedCount || 0} skipped`}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {`${completenessSummary?.unknownCount || 0} marked unknown`}
        </Typography>
      </Box>
      {items.map((item, idx) => (
        <Box
          key={`completeness-${idx}`}
          display="flex"
          justifyContent="space-between"
          py={0.75}
          style={{
            borderBottom: `1px solid ${palette.backgroundGray.main}`
          }}
        >
          <Typography variant="body2" color="textPrimary">
            {item.label}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const DataGapsBlock = ({ dataGapsList, palette }) => (
  <Box
    mx={5}
    mt={3}
    p={3}
    style={{
      backgroundColor: palette.backgroundGray.light,
      borderRadius: "4px"
    }}
  >
    <Typography variant="subtitle2" color="textPrimary" gutterBottom>
      Sections you skipped or marked unknown
    </Typography>
    {dataGapsList.map((label, idx) => (
      <Typography key={`gap-${idx}`} variant="body2" color="textSecondary">
        {`• ${label} — Answer this now in Aclymate Explorer to refine your estimate.`}
      </Typography>
    ))}
  </Box>
);

const NextStepsBlock = ({ recommendedNextSteps, palette }) => (
  <Box mx={5} mt={3}>
    <Typography variant="h5" color="textPrimary" gutterBottom>
      Recommended next steps
    </Typography>
    {recommendedNextSteps.map((step, idx) => (
      <Box
        key={`step-${idx}`}
        py={1.5}
        style={{
          borderBottom: `1px solid ${palette.backgroundGray.main}`
        }}
      >
        <Typography variant="body2" color="textPrimary">
          {`${idx + 1}. ${step}`}
        </Typography>
      </Box>
    ))}
  </Box>
);

const CtaBlock = ({ palette }) => (
  <Box
    mx={5}
    mt={3}
    p={3}
    display="flex"
    flexDirection="column"
    gap={1}
    style={{
      backgroundColor: palette.primary.main,
      borderRadius: "4px",
      color: palette.common.white
    }}
  >
    <Typography variant="h6" color="inherit">
      Ready to measure, not estimate?
    </Typography>
    <Typography variant="body2" color="inherit">
      Talk to an Aclymate sustainability expert for a 30-minute orientation, or
      upgrade to Aclymate Navigator for measurement-grade carbon accounting.
    </Typography>
    <Box display="flex" gap={2} mt={1}>
      <Link
        href={TALK_TO_EXPERT_URL}
        underline="always"
        styleProps={{ color: palette.common.white }}
      >
        <Typography variant="body2" color="inherit">
          Talk to an Aclymate expert
        </Typography>
      </Link>
      <Link
        href={UPGRADE_URL}
        underline="always"
        styleProps={{ color: palette.common.white }}
      >
        <Typography variant="body2" color="inherit">
          Upgrade to Aclymate Navigator
        </Typography>
      </Link>
    </Box>
  </Box>
);

const FooterBlock = ({ companyName }) => (
  <Box mx={5} mt={3}>
    <AclymateFooter companyName={companyName} isLocalImage />
  </Box>
);

const ContentPageLayout = ({
  companyName,
  year,
  palette,
  blocks,
  setRefElement,
  showHeader
}) => (
  <PdfPage palette={palette}>
    {showHeader && (
      <PageHeader companyName={companyName} year={year} palette={palette} />
    )}
    <Box flexGrow={1}>
      {blocks.map((block, idx) => (
        <div
          key={`content-block-${idx}`}
          ref={(element) => setRefElement && setRefElement(element, idx)}
        >
          {block}
        </div>
      ))}
    </Box>
  </PdfPage>
);

const WrappedPages = ({ companyName, year, palette, blocks }) => {
  const { pageBlocks, setRefElement } = useDynamicWrappingPages(blocks);

  if (pageBlocks.length <= 1) {
    return (
      <ContentPageLayout
        companyName={companyName}
        year={year}
        palette={palette}
        blocks={blocks}
        setRefElement={setRefElement}
        showHeader
      />
    );
  }

  return (
    <>
      {pageBlocks.map((pageBlocksForPage, idx) => (
        <ContentPageLayout
          key={`content-page-${idx}`}
          companyName={companyName}
          year={year}
          palette={palette}
          blocks={pageBlocksForPage}
          showHeader
        />
      ))}
    </>
  );
};

const PdfReport = ({
  companyName,
  year,
  generatedDate,
  totalTons,
  scopePieSeries = [],
  sourcePieSeries = [],
  completenessSummary,
  dataGapsList = [],
  recommendedNextSteps = []
}) => {
  const { palette } = useTheme();
  const scopeData = buildSliceData(scopePieSeries, palette);
  const sourceData = buildSliceData(collapseExtras(sourcePieSeries), palette);

  const hasGaps = dataGapsList && dataGapsList.length > 0;

  const contentBlocks = [
    <BreakdownBlock
      key="scope-breakdown"
      title={`Scope 1, 2, 3 breakdown — ${year}`}
      data={scopeData}
      palette={palette}
    />,
    <BreakdownBlock
      key="activity-breakdown"
      title={`Emissions by activity — ${year}`}
      data={sourceData}
      palette={palette}
    />,
    <CompletenessBlock
      key="completeness"
      completenessSummary={completenessSummary}
      palette={palette}
    />,
    ...(hasGaps
      ? [
          <DataGapsBlock
            key="data-gaps"
            dataGapsList={dataGapsList}
            palette={palette}
          />
        ]
      : []),
    <NextStepsBlock
      key="next-steps"
      recommendedNextSteps={recommendedNextSteps}
      palette={palette}
    />,
    <CtaBlock key="cta" palette={palette} />,
    <FooterBlock key="footer" companyName={companyName} />
  ];

  return (
    <Box
      style={{
        width: PAGE_WIDTH,
        backgroundColor: palette.common.white,
        fontFamily: "inherit"
      }}
    >
      <CoverPage
        companyName={companyName}
        generatedDate={generatedDate}
        year={year}
        totalTons={totalTons}
        palette={palette}
      />
      <WrappedPages
        companyName={companyName}
        year={year}
        palette={palette}
        blocks={contentBlocks}
      />
    </Box>
  );
};

export default PdfReport;
